import { RemoteConfigModel } from 'src/models/remoteConfig.models'

/**
 * Check if AI Insights feature is enabled for the user.
 * Returns true if either:
 * 1. The global show_ai_insights flag is enabled, OR
 * 2. The user's organization ID is in the ai_insights_allowlist
 *
 * @param remoteConfig - The remote config model from the feature flagger
 * @param organizationId - The user's organization ID
 * @returns true if AI Insights is enabled for this user
 */
export const isAIInsightsEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
  organizationId: string | undefined,
): boolean => {
  if (!remoteConfig || !organizationId) {
    return false
  }

  const { show_ai_insights, ai_insights_allowlist } = remoteConfig

  // Feature is enabled if the global flag is on OR the org is in the allowlist
  return show_ai_insights || ai_insights_allowlist.includes(organizationId)
}

/**
 * Check if Extend AI Agent feature is enabled for the user.
 * Returns true if either:
 * 1. The global show_extend_ai_agent flag is enabled, OR
 * 2. The user's organization ID is in the extendAIWhitelist
 *
 * @param remoteConfig - The remote config model from the feature flagger
 * @param organizationId - The user's organization ID
 * @returns true if Extend AI Agent is enabled for this user
 */
export const isExtendAIEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
  organizationId: string | undefined,
): boolean => {
  if (!remoteConfig || !organizationId) {
    return false
  }

  const { show_extend_ai_agent, extendAIWhitelist } = remoteConfig

  // Feature is enabled if the global flag is on OR the org is in the allowlist
  return show_extend_ai_agent || extendAIWhitelist.includes(organizationId)
}

/**
 * Check if Normalized Merchant feature is enabled for the user.
 * Returns true if either:
 * 1. The global show_normalized_merchant flag is enabled, OR
 * 2. The user's organization ID is in the normalizedMerchantWhitelist
 *
 * @param remoteConfig - The remote config model from the feature flagger
 * @param organizationId - The user's organization ID
 * @returns true if Normalized Merchant is enabled for this user
 */
export const isNormalizedMerchantEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
  organizationId: string | undefined,
): boolean => {
  if (!remoteConfig || !organizationId) {
    return false
  }

  const { show_normalized_merchant, normalizedMerchantWhitelist } = remoteConfig

  // Feature is enabled if the global flag is on OR the org is in the allowlist
  return (
    show_normalized_merchant ||
    normalizedMerchantWhitelist.includes(organizationId)
  )
}

/**
 * Check if Suggestions and Predictions feature is enabled for the user.
 * Returns true if either:
 * 1. The global show_suggestions_and_predictions flag is enabled, OR
 * 2. The user's organization ID is in the suggestionsAndPredictionsWhitelist
 *
 * @param remoteConfig - The remote config model from the feature flagger
 * @param organizationId - The user's organization ID
 * @returns true if Suggestions and Predictions is enabled for this user
 */
export const isSuggestionsAndPredictionsEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
  organizationId: string | undefined,
): boolean => {
  if (!remoteConfig || !organizationId) {
    return false
  }

  const {
    show_suggestions_and_predictions,
    suggestionsAndPredictionsWhitelist,
  } = remoteConfig

  // Feature is enabled if the global flag is on OR the org is in the allowlist
  return (
    show_suggestions_and_predictions ||
    suggestionsAndPredictionsWhitelist.includes(organizationId)
  )
}
